import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { ModelType } from '~/anonymizer/anonymizer';
import { RequestParameters } from '~/backend/backend-client';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useSessionID, useTranslationBackend } from '../useBackend';

/**
 * A React hook for creating an image translation task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useImageTranslation() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useRecoilValue(primaryImageState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const imageTranslationClient = useTranslationBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentBlendImage, setEnvironmentStructureSet } =
        useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results, ModelType.Translation)
                : job.results;

            if (!finalFiles) {
                handleError('Re-identification failed for one or more files.');
                return;
            }

            const processedItems = await processCreatedFiles(
                finalFiles,
                job.task!.model
            );
            if (!processedItems) return;

            updateJob(job.id, { isImported: true });
            const generatedStructureSet = processedItems.structureSets[0];
            const generatedImage = processedItems.images[0];
            const currentPrimaryImage = primaryImage;

            if (generatedStructureSet) {
                setEnvironmentStructureSet(
                    [generatedStructureSet],
                    selectedPatientId ?? ''
                );

                if (generatedStructureSet.image === generatedImage) {
                    // Structure set references the generated image
                    setEnvironmentBlendImage(currentPrimaryImage!);
                } else {
                    // Structure set references input image
                    setEnvironmentBlendImage(generatedImage);
                }
            } else {
                setEnvironmentBlendImage(generatedImage);
            }
        }
    };

    const handleSubmit = async (
        selectedModel: PredictionModel,
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        if (primaryImage) {
            try {
                setIsSubmitting(true);
                resetSessionID();
                const blobs = Array.from(primaryImage.getSlicesAsDicomBlobs());
                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(blobs, ModelType.Translation)
                    : blobs;

                const modelIdentifier = customizationName
                    ? `${selectedModel.name}.${customizationName}`
                    : selectedModel.name;

                const backendParameters: RequestParameters = {
                    action: modelIdentifier,
                    taskUID: `${sessionID}.${v4().split('-')[0]}`,
                    nExpectedFiles: blobs.length.toString(),
                };

                if (blobsToUpload) {
                    const newJobDetails: NewJobData = {
                        id: sessionID,
                        name: 'Image+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.IMAGE,
                        client: imageTranslationClient,
                        onLoad: handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: selectedModel,
                        },
                    };

                    addJob(newJobDetails);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error, 'Failed to start image translation task.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return { startProcessing: handleSubmit, isSubmitting };
}
